import React from 'react';
import { Modal, Button, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';

interface TeamDetailsModalProps {
  team: any | null;
  isOpen: boolean;
  onClose: () => void;
}

const TeamDetailsModal: React.FC<TeamDetailsModalProps> = ({
  team,
  isOpen,
  onClose
}) => {
  if (!team) return null;

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label} copied to clipboard!`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`${label} copied to clipboard!`);
      } catch (fallbackErr) {
        message.error('Failed to copy to clipboard');
      }
      document.body.removeChild(textArea);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get team leader and member from participants array
  const teamLeader = team.participants?.find((participant: any) => participant.leader);
  const teamMember = team.participants?.find((participant: any) => !participant.leader);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="team-details-modal"
      styles={{
        mask: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
      }}
    >
      <div className="bg-white rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Team Details</h2>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Team Overview */}
          <div className="mb-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Team Overview</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">Team Name</p>
                  <p className="text-sm text-gray-600">{team.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Date Created</p>
                  <p className="text-sm text-gray-600">{formatDate(team.createdAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold text-gray-900">Team Members</h3>
            
            <div className="grid grid-cols-2 gap-4 w-full">
              {/* Team Leader */}
              {teamLeader && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {/* Profile Picture */}
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-lg font-semibold text-gray-600">
                          {teamLeader.userId?.fullName?.charAt(0).toUpperCase() || 'L'}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">
                          {teamLeader.userId?.fullName || 'N/A'}
                        </h4>
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          Team Leader
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {/* Email Address */}
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Email Address</p>
                        <p className="text-sm text-gray-600">{teamLeader.userId?.email || 'N/A'}</p>
                      </div>
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(teamLeader.userId?.email || '', 'Email')}
                        className="text-gray-500 hover:text-gray-700"
                        size="small"
                      />
                    </div>

                    {/* Phone Number */}
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Phone Number</p>
                        <p className="text-sm text-gray-600">{teamLeader.userId?.phoneNumber || 'N/A'}</p>
                      </div>
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(teamLeader.userId?.phoneNumber || '', 'Phone number')}
                        className="text-gray-500 hover:text-gray-700"
                        size="small"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Team Member */}
              {teamMember && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {/* Profile Picture */}
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-lg font-semibold text-gray-600">
                          {teamMember.userId?.fullName?.charAt(0).toUpperCase() || 'M'}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">
                          {teamMember.userId?.fullName || 'N/A'}
                        </h4>
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          Team Member
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {/* Email Address */}
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Email Address</p>
                        <p className="text-sm text-gray-600">{teamMember.userId?.email || 'N/A'}</p>
                      </div>
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(teamMember.userId?.email || '', 'Email')}
                        className="text-gray-500 hover:text-gray-700"
                        size="small"
                      />
                    </div>

                    {/* Phone Number */}
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Phone Number</p>
                        <p className="text-sm text-gray-600">{teamMember.userId?.phoneNumber || 'N/A'}</p>
                      </div>
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(teamMember.userId?.phoneNumber || '', 'Phone number')}
                        className="text-gray-500 hover:text-gray-700"
                        size="small"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* No members message */}
              {!teamLeader && !teamMember && (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-2">👥</div>
                  <p className="text-gray-500">No team members found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TeamDetailsModal;